// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// --- RxJs
import { catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

// --- 3rd Party
import { retryBackoff } from 'backoff-rxjs';
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { environment } from '../../../../environments/environment';
import {
  ApplicationError,
  ErrorMessage,
  SurveyResponseContexts,
  SurveyResultsModel,
  SurveyStatuses,
} from '../../../models';
import { AppService } from '../app.service';
import { ResponseBaseService } from '../survey/response-base.service';
import { ResultsBaseService } from './results-base.service';

// #endregion

@Injectable({
  providedIn: 'root',
})
export class ResultsService extends ResultsBaseService {
  constructor(
    private http: HttpClient,
    private appService: AppService,
    private translate: TranslateService,
    private responseService: ResponseBaseService
  ) {
    super();
    this.responseService.activeResponse$.subscribe((response) => {
      if (!response) {
        return;
      }

      if (response.responseContext === SurveyResponseContexts.respondent) {
        return;
      }

      if (response.status === SurveyStatuses.completeSurvey) {
        this.loadSurveyResults(response.surveyId);
      }
    });
  }

  loadSurveyResults(identifier: string): void {
    if (!identifier) {
      this._areResultsLoading$.next(false);
      return;
    }

    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.resultsApi +
      `/${identifier}`;
    this.http
      .get<SurveyResultsModel>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._resultsModelError.bind(this))
      )
      .subscribe((results) => {
        if (results) {
          this._surveyResults$.next(results);
          this._mapScores(results);
          this._areResultsLoading$.next(false);
        }
      });
  }

  private _resultsModelError(err: any): Observable<SurveyResultsModel | null> {
    this._handleError(err);
    return of(null);
  }

  private _handleError(err: any) {
    this.appService.setLoading(false);

    const errorMessage: string = this.translate.instant('errorMessageGenericBackend');

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'ResultsService',
      true,
      err
    );

    this.appService.setLastError(error);
  }
}
